import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import config from '../config';
import RuleScreen from '../shared/RuleScreen';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const calcDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms)) return 'Invalid';
  const min = Math.abs(ms / 60000);
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

const pctCell = (b, highlight) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: highlight ? 'var(--danger)' : 'var(--muted)', fontWeight: highlight ? 700 : 400, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

export default function MultiSpouse({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, filter: 'all', records: [], loading: false });
  const [membersPopup, setMembersPopup] = useState({ open: false, record: null, members: [], loading: false });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/multi-spouse/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching multi-spouse data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalSurveys = summary?.totalSurveys ?? rows.reduce((s, r) => s + (Number(r.totalSurveys) || 0), 0);

  const tiles = (
    <>
      <StatTile label="Total Surveys" value={totalSurveys} icon={GroupsIcon} accent="var(--primary)" />
      <StatTile
        label=">1 Husband or Wife"
        value={`${summary?.multiSpouse?.pct ?? 0}%`}
        icon={WarningAmberIcon}
        accent={(summary?.multiSpouse?.pct ?? 0) >= 10 ? 'var(--danger)' : (summary?.multiSpouse?.pct ?? 0) >= 5 ? 'var(--warning)' : 'var(--info)'}
        sub={`${summary?.multiSpouse?.count ?? 0} of ${totalSurveys} households`}
      />
    </>
  );

  const openDrilldown = async (row, filter = 'all') => {
    setDrawer({ open: true, row, filter, records: [], loading: true });
    try {
      const params = buildParams(applied, {
        extra: { groupBy: activeTab, groupValue: row.name, filter, limit: 100 },
      });
      const res = await axios.get(`${API_URL}/multi-spouse/details?${params}`);
      const records = res.data?.data || [];
      setDrawer({ open: true, row, filter, records, loading: false });
    } catch (err) {
      console.error('Error fetching multi-spouse detail records:', err);
      setDrawer({ open: true, row, filter, records: [], loading: false });
    }
  };

  const multiSpouseCell = (row) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, 'multiSpouse'); }}>
      {pctCell(row.multiSpouse, true)}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys || 0}</span> },
    { key: 'multiSpouse', label: '>1 Husband or Wife (% of Total)', align: 'center', render: (r) => multiSpouseCell(r) },
  ];

  const openMembersPopup = async (record) => {
    setMembersPopup({ open: true, record, members: [], loading: true });
    try {
      const res = await axios.get(`${API_URL}/multi-spouse/members?uuid=${encodeURIComponent(record.uuid)}`);
      setMembersPopup({ open: true, record, members: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching household members:', err);
      setMembersPopup({ open: true, record, members: [], loading: false });
    }
  };

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    {
      key: 'household_id', label: 'Household ID',
      render: (r) => (
        <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openMembersPopup(r); }}>
          {r.household_id || '—'}
        </span>
      ),
    },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'husbands', label: 'Husbands', align: 'center', render: (r) => <span className={r.husbands > 1 ? 'cell-strong' : ''} style={r.husbands > 1 ? { color: 'var(--danger)' } : undefined}>{r.husbands ?? 0}</span> },
    { key: 'wives', label: 'Wives', align: 'center', render: (r) => <span className={r.wives > 1 ? 'cell-strong' : ''} style={r.wives > 1 ? { color: 'var(--danger)' } : undefined}>{r.wives ?? 0}</span> },
    { key: 'block', label: 'Block', render: (r) => r.block || '—' },
    { key: 'village_name', label: 'Village', render: (r) => r.village_name || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
    { key: 'end', label: 'End Time', render: (r) => formatDate(r.end) },
    { key: 'duration', label: 'Duration', align: 'center', render: (r) => calcDuration(r.start, r.end) },
  ];

  const filterBar = (
    <FilterBar
      onApply={(v) => { setApplied(v); setPage(1); }}
      onClear={() => { setApplied(EMPTY); setPage(1); }}
      loading={loading}
    />
  );

  return (
    <RuleScreen
      title={rule?.title || '>1 Husband or Wife in Household'}
      subtitle="Flags households with more than one member recorded as Husband or Wife."
      icon={PeopleAltIcon}
      backTo={rule?.category ? `/category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {filterBar}
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Multiple Spouses by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          onRowClick={(r) => openDrilldown(r, 'all')}
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${drawer.filter === 'multiSpouse' ? '>1 Husband or Wife' : 'All Surveys'}` : ''}
        sections={
          drawer.row
            ? [
                {
                  title: 'Breakdown',
                  items: [
                    { label: 'Total Surveys', value: drawer.row.totalSurveys || 0 },
                    { label: '>1 Husband or Wife', value: `${drawer.row.multiSpouse?.count || 0} (${drawer.row.multiSpouse?.pct || 0}%)` },
                  ],
                },
              ]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: '-0.4rem' }}>
            Click a household ID to see its full member list.
          </p>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            loading={drawer.loading}
            emptyMessage="No households found."
            getRowKey={(r, i) => r.uuid || i}
          />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={membersPopup.open}
        onClose={() => setMembersPopup((p) => ({ ...p, open: false }))}
        title={membersPopup.record ? `Members: ${membersPopup.record.household_id}` : ''}
        sections={
          membersPopup.record
            ? [{ title: 'Household', items: [
                { label: 'Respondent', value: membersPopup.record.respondent_name },
                { label: 'Husbands', value: membersPopup.record.husbands },
                { label: 'Wives', value: membersPopup.record.wives },
              ] }]
            : []
        }
      >
        <div className="detail-section">
          <h4>Members ({membersPopup.members.length})</h4>
          <DataTable
            columns={[
              { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
              {
                key: 'name', label: 'Name',
                render: (m) => <span className={m.isSpouse ? 'cell-strong' : ''} style={m.isSpouse ? { color: 'var(--danger)' } : undefined}>{m.name || '—'}</span>,
              },
              { key: 'relation', label: 'Relation', render: (m) => m.relation || '—' },
              { key: 'gender', label: 'Gender', render: (m) => m.gender || '—' },
              { key: 'age', label: 'Age', align: 'center', render: (m) => m.age ?? '—' },
              {
                key: 'isSpouse', label: 'Status', align: 'center',
                render: (m) => (
                  <span className={`badge ${m.isSpouse ? 'badge-danger' : 'badge-neutral'}`}>
                    {m.isSpouse ? 'Husband/Wife' : '—'}
                  </span>
                ),
              },
            ]}
            rows={membersPopup.members}
            loading={membersPopup.loading}
            emptyMessage="No members found for this household."
            getRowKey={(m, i) => `${m.name}-${i}`}
          />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
